import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Drawer,
  Input,
  Select,
  Table,
  Tag,
  message,
} from '@surecompanies/core_components'
import ApiOutlined from '@ant-design/icons/ApiOutlined'
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined'
import SearchOutlined from '@ant-design/icons/SearchOutlined'
import { useQuery } from '@tanstack/react-query'
import {
  CarrierGroup,
  getCarrierConnection,
  getCarriers,
  type CarrierConnectionPlan,
  type CarrierConnectionResponse,
} from '../api/fetch-carrier-api'
import styles from '../PlanManagementV3.module.scss'

const ZIP_REGEX = /^\d{5}$/
const FIPS_REGEX = /^\d{5}$/

const ERROR_HINTS: Record<string, string> = {
  GEO_BLOCKED:
    'Check your VPN connection — this carrier requires a US-based IP.',
  BOT_DETECTION: 'Session headers may need updating — contact engineering.',
  TIMEOUT: 'Carrier did not respond in time. Try again later.',
  CONNECTION_REFUSED:
    'Cannot reach carrier URL. Check DNS/availability or wait for carrier to come back.',
  SESSION_FAILED:
    'Carrier may have changed their auth flow. Contact engineering.',
}

interface Props {
  open: boolean
  onClose: () => void
  defaultGroupCode?: string
}

export default function CarrierConnectionDrawer({
  open,
  onClose,
  defaultGroupCode,
}: Props) {
  const [groupCode, setGroupCode] = useState<string | undefined>(
    defaultGroupCode
  )
  const [zip, setZip] = useState('')
  const [fipsCode, setFipsCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<CarrierConnectionResponse | null>(null)
  const [testedAt, setTestedAt] = useState<Date | null>(null)
  const [planFilter, setPlanFilter] = useState('')
  const [plansPage, setPlansPage] = useState(1)

  const { data: carriers, isLoading: isLoadingCarriers } = useQuery({
    queryKey: ['planBotCarriers', 'active'],
    queryFn: () => getCarriers({ isActive: true }),
    enabled: open,
  })

  const carrierOptions = useMemo(() => {
    const list = Array.isArray(carriers) ? carriers : []
    return Array.from(
      new Map(
        list.map((c) => [
          c.groupCode,
          {
            value: c.groupCode,
            label: c.carrierName ?? c.carrierFullName ?? c.groupCode,
          },
        ])
      ).values()
    )
  }, [carriers])

  useEffect(() => {
    if (open) setGroupCode(defaultGroupCode)
  }, [open, defaultGroupCode])

  useEffect(() => {
    setResult(null)
    setTestedAt(null)
    setPlanFilter('')
    setPlansPage(1)
  }, [groupCode])

  const filteredPlans = useMemo(() => {
    if (!result) return []
    const query = planFilter.trim().toLowerCase()
    if (!query) return result.plans
    return result.plans.filter(
      (p) =>
        p.hiosId?.toLowerCase().includes(query) ||
        p.planName?.toLowerCase().includes(query)
    )
  }, [result, planFilter])

  // Jump back to page 1 whenever the search text changes, so a stale page
  // number from a previous (larger) result set can't leave the table
  // looking empty or showing leftover rows from before the filter changed.
  useEffect(() => {
    setPlansPage(1)
  }, [planFilter])

  const isZipValid = ZIP_REGEX.test(zip)
  const isFipsValid = FIPS_REGEX.test(fipsCode)

  const planColumns = useMemo(
    () => [
      {
        title: 'HIOS Plan ID',
        dataIndex: 'hiosId',
        key: 'hiosId',
        width: 160,
      },
      {
        title: 'Plan name',
        dataIndex: 'planName',
        key: 'planName',
      },
      {
        title: 'Base rate',
        dataIndex: 'premium',
        key: 'premium',
        width: 90,
        render: (v: number) =>
          `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
      },
      {
        title: 'Metal',
        dataIndex: 'metalLevel',
        key: 'metalLevel',
        width: 80,
      },
      {
        title: 'HSA',
        dataIndex: 'hsaEligible',
        key: 'hsaEligible',
        width: 50,
        render: (v: boolean) => (v ? '✓' : '–'),
      },
      {
        title: 'Exchange',
        key: 'exchange',
        width: 80,
        render: (_: unknown, row: CarrierConnectionPlan) =>
          row.onExchange ? 'On' : 'Off',
      },
      {
        title: 'For sale',
        dataIndex: 'isForSale',
        key: 'isForSale',
        width: 70,
        render: (v: boolean) =>
          v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>,
      },
      {
        title: 'Zip code',
        key: 'zipCode',
        width: 90,
        render: () => zip,
      },
      {
        title: 'FIPS code',
        key: 'fipsCode',
        width: 100,
        render: (_: unknown, row: CarrierConnectionPlan) =>
          row.fips ?? fipsCode,
      },
    ],
    [zip, fipsCode]
  )

  const handleRunTest = useCallback(async () => {
    if (!isZipValid || !isFipsValid || !groupCode) return
    setLoading(true)
    setResult(null)
    setPlanFilter('')
    setPlansPage(1)
    try {
      const res = await getCarrierConnection({
        groupCode: groupCode as CarrierGroup,
        zip,
        fipsCode,
      })
      setResult(res)
      setTestedAt(new Date())
    } catch (err) {
      console.error('Carrier connection test failed', err)
      message.error('Request failed. Check network and try again.')
    } finally {
      setLoading(false)
    }
  }, [groupCode, zip, fipsCode, isZipValid, isFipsValid])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <Drawer
      title="Carrier connection"
      placement="right"
      width={800}
      open={open}
      onClose={handleClose}
      destroyOnClose
    >
      <p className={styles.connectionDescription}>
        Pings the carrier&apos;s live website directly. No data is saved to the
        DB.
      </p>

      <div className={styles.connectionForm}>
        <div className={styles.connectionFormField}>
          <label className={styles.connectionFormLabel}>Carrier</label>
          <Select
            style={{ width: '100%' }}
            placeholder="Select a carrier"
            showSearch
            optionFilterProp="label"
            loading={isLoadingCarriers}
            value={groupCode}
            onChange={(v) => setGroupCode(v as string)}
            options={carrierOptions}
          />
        </div>
        <div className={styles.connectionFormField}>
          <label className={styles.connectionFormLabel}>Zip code</label>
          <Input
            placeholder="32040"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            maxLength={5}
            style={{ width: '100%' }}
          />
        </div>
        <div className={styles.connectionFormField}>
          <label className={styles.connectionFormLabel}>
            FIPS code <span className={styles.connectionFormRequired}>*</span>
          </label>
          <Input
            placeholder="12109"
            value={fipsCode}
            onChange={(e) => setFipsCode(e.target.value)}
            maxLength={5}
            style={{ width: '100%' }}
          />
        </div>
        <div className={styles.connectionFormAction}>
          <Button
            type="primary"
            icon={<ApiOutlined />}
            loading={loading}
            disabled={!groupCode || !isZipValid || !isFipsValid || loading}
            onClick={handleRunTest}
          >
            Run test
          </Button>
        </div>
      </div>

      {result && (
        <div className={styles.connectionResult}>
          <div className={styles.connectionResultHeader}>
            {result.connected ? (
              <span className={styles.connectionBadgeSuccess}>
                <CheckCircleOutlined /> Connected
              </span>
            ) : (
              <span className={styles.connectionBadgeFail}>
                <CloseCircleOutlined /> Failed
              </span>
            )}
            {testedAt && (
              <span className={styles.connectionTimestamp}>
                {testedAt.toLocaleString()}
              </span>
            )}
          </div>

          {!result.connected &&
            result.errorCategory &&
            ERROR_HINTS[result.errorCategory] && (
              <Alert
                type="error"
                showIcon
                message={ERROR_HINTS[result.errorCategory]}
                className={styles.connectionErrorHint}
              />
            )}

          <div className={styles.connectionMeta}>
            <div className={styles.connectionMetaItem}>
              <span className={styles.connectionMetaLabel}>Endpoint</span>
              <span className={styles.connectionMetaValue}>
                {result.endpoint}
              </span>
            </div>
            <div className={styles.connectionMetaItem}>
              <span className={styles.connectionMetaLabel}>HTTP status</span>
              <span className={styles.connectionMetaValue}>
                {result.httpStatus != null
                  ? `${result.httpStatus} ${result.httpStatus === 200 ? 'OK' : ''}`
                  : '—'}
              </span>
            </div>
            <div className={styles.connectionMetaItem}>
              <span className={styles.connectionMetaLabel}>Response time</span>
              <span className={styles.connectionMetaValue}>
                {result.responseTimeMs} ms
              </span>
            </div>
            <div className={styles.connectionMetaItem}>
              <span className={styles.connectionMetaLabel}>Plans returned</span>
              <span className={styles.connectionMetaValue}>
                {result.plansReturned}
              </span>
            </div>
          </div>

          {!result.connected && result.error && (
            <div className={styles.connectionErrorMessage}>{result.error}</div>
          )}

          {result.plans.length > 0 && (
            <div className={styles.connectionPlansSection}>
              <div className={styles.connectionPlansHeader}>
                <span className={styles.connectionPlansTitle}>
                  Plans ({filteredPlans.length} of {result.plansReturned})
                </span>
                <Input
                  placeholder="Filter by HIOS Plan ID or plan name"
                  prefix={<SearchOutlined />}
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  allowClear
                  style={{ width: 260 }}
                />
              </div>
              <Table<CarrierConnectionPlan>
                className={styles.flatTable}
                dataSource={filteredPlans}
                columns={planColumns}
                rowKey={(row, index) =>
                  `${row.hiosId}-${row.fips ?? 'na'}-${index}`
                }
                size="small"
                pagination={{
                  current: plansPage,
                  pageSize: 10,
                  size: 'small',
                  showSizeChanger: false,
                  onChange: (page) => setPlansPage(page),
                }}
                scroll={{ x: 890 }}
              />
            </div>
          )}
        </div>
      )}
    </Drawer>
  )
}
